import { ArrowRight, Smartphone, Globe, Shield, Zap, BookOpen, Video, FileText, Settings } from 'lucide-react';
import { useLang } from '@/context/LangContext';

const PHOTO = "https://customer-assets.emergentagent.com/job_lang-assist-2/artifacts/jdfld03k_ChatGPT%20Image%2024%20Nis%202026%2015_53_14.png";

const Home = () => {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="min-h-screen flex items-center pt-20 bg-gradient-to-br from-white via-[#FDFBF7] to-[#F8F0E3]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.1] text-[#0A0A0A] mb-6">
                {t.hero.title1}<br /><span className="text-[#C19B76] italic font-normal">{t.hero.title2}</span>
              </h1>
              <p className="text-base sm:text-lg font-light leading-relaxed text-[#555] mb-10 max-w-lg">{t.hero.desc}</p>
              <a href="https://aurem8.tentary.com/" target="_blank" rel="noopener noreferrer" className="inline-flex bg-[#C19B76] text-white rounded-full px-8 py-4 font-medium hover:bg-[#A6825B] transition-colors items-center gap-2 text-sm" data-testid="hero-cta">
                {t.hero.cta}<ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="flex justify-center">
              <div className="w-80 h-80 md:w-[420px] md:h-[420px] rounded-full overflow-hidden border-4 border-[#C19B76]/20 shadow-2xl">
                <img src={PHOTO} alt="Nihal Yayla" className="w-full h-full object-cover object-top" data-testid="hero-photo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Preview */}
      <section className="py-24 md:py-32 bg-white" data-testid="story-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-[#C19B76] mb-4">{t.story.overline}</p>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#0A0A0A] mb-8">{t.story.title}</h2>
          <p className="text-lg font-light leading-relaxed text-[#555] mb-4">{t.story.p1}</p>
          <p className="text-lg font-light leading-relaxed text-[#555] mb-4">{t.story.p2}</p>
          <p className="text-lg font-light leading-relaxed text-[#555]">{t.story.p4}</p>
        </div>
      </section>

      {/* OWA Intro */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="owa-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-[#C19B76] mb-4">{t.owa_intro.overline}</p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#0A0A0A] mb-6">{t.owa_intro.title}</h2>
            <p className="text-lg font-light text-[#555] max-w-3xl mx-auto">{t.owa_intro.desc}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {t.owa_intro.features.map((f, i) => (
              <div key={i} className="flex items-start gap-4 bg-white rounded-2xl p-6 border border-[#C19B76]/10">
                {[Smartphone, Globe, Shield, Zap][i] && (() => { const Icon = [Smartphone, Globe, Shield, Zap][i]; return <Icon className="w-6 h-6 text-[#C19B76] mt-0.5 flex-shrink-0" strokeWidth={1.5} />; })()}
                <span className="text-sm text-[#444] leading-relaxed">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Products */}
      <section className="py-24 md:py-32 bg-white" data-testid="digital-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-medium text-[#C19B76] mb-4">{t.digital.overline}</p>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#0A0A0A] mb-6">{t.digital.title}</h2>
              <p className="text-lg font-light text-[#555] mb-8">{t.digital.desc}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {t.digital.items.map((item, i) => (
                <div key={i} className="bg-[#FDFBF7] rounded-2xl p-6 border border-[#C19B76]/10 text-center">
                  {[BookOpen, Video, FileText, Settings][i] && (() => { const Icon = [BookOpen, Video, FileText, Settings][i]; return <Icon className="w-8 h-8 text-[#C19B76] mx-auto mb-3" strokeWidth={1.5} />; })()}
                  <span className="text-sm font-medium text-[#333]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MRR */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="mrr-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-[#C19B76] mb-4">{t.mrr.overline}</p>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#0A0A0A] mb-6">{t.mrr.title}</h2>
          <p className="text-lg font-light text-[#555]">{t.mrr.desc}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-white mb-6">{t.cta_section.title}</h2>
          <p className="text-lg font-light text-white/60 mb-10">{t.cta_section.desc}</p>
          <a href="https://aurem8.tentary.com/" target="_blank" rel="noopener noreferrer" className="inline-flex bg-[#C19B76] text-white rounded-full px-10 py-4 font-medium hover:bg-[#A6825B] transition-colors items-center gap-2" data-testid="final-cta">
            {t.cta_section.btn}<ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
